import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { 
  BarChart3, Users, Building2, Package, CheckCircle2, XCircle, Search, Megaphone, Crown,
  Loader2, Eye, Trash2, Mail, Send, AlertCircle, Clock, TrendingUp, Calendar, Plus,
  RefreshCw, History, AlertTriangle, FileText, ShoppingCart, MessageSquare, Gift, EyeOff, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tier colors and icons
const TIER_CONFIG = {
  gold: { color: 'text-yellow-500', bg: 'bg-yellow-100', border: 'border-yellow-300', icon: '🥇' },
  silver: { color: 'text-slate-400', bg: 'bg-slate-100', border: 'border-slate-300', icon: '🥈' },
  bronze: { color: 'text-orange-600', bg: 'bg-orange-100', border: 'border-orange-300', icon: '🥉' }
};

const AdminPanel = () => {
  const { user } = useAuth();
  const { isUSA } = useLocale();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('companies');
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [showHiddenCompanies, setShowHiddenCompanies] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [expiringSoon, setExpiringSoon] = useState([]);
  const [sponsorHistory, setSponsorHistory] = useState([]);
  const [tierPricing, setTierPricing] = useState({});
  // Email queue state
  const [emailStats, setEmailStats] = useState(null);
  const [emailQueue, setEmailQueue] = useState([]);
  const [emailFilter, setEmailFilter] = useState('all'); // 'all', 'pending', 'sent', 'failed'
  const [selectedEmail, setSelectedEmail] = useState(null); // For viewing email details
  const [searchQuery, setSearchQuery] = useState('');
  
  // Catalog reminders state
  const [catalogReminders, setCatalogReminders] = useState({ pending_count: 0, users: [], config: {} });
  const [sendingReminders, setSendingReminders] = useState(false);
  
  // Referrals state
  const [referralData, setReferralData] = useState({ summary: {}, users: [] });
  
  // Users management
  const [users, setUsers] = useState([]);
  const [promoUsers, setPromoUsers] = useState([]);
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [userDetailDialog, setUserDetailDialog] = useState({ open: false, user: null, loading: false, data: null });
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    name: '',
    password: '',
    subscription_plan: 'free',
    apply_launch_promo: false
  });
  
  // Dialogs
  const [sponsorDialog, setSponsorDialog] = useState({ open: false, company: null, mode: 'create' });
  const [extendDialog, setExtendDialog] = useState({ open: false, sponsor: null });
  const [metricsDialog, setMetricsDialog] = useState({ open: false, sponsor: null });
  const [historyDialog, setHistoryDialog] = useState({ open: false });
  
  // Forms
  const [sponsorForm, setSponsorForm] = useState({ tier: 'bronze', days: 30 });
  const [extendDays, setExtendDays] = useState(30);
  
  // Processing states
  const [processing, setProcessing] = useState(false);
  const [processingEmails, setProcessingEmails] = useState(false);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token')}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, companiesRes, sponsorsRes, expiringRes, tiersRes, emailStatsRes, emailQueueRes, usersRes, promoUsersRes, remindersRes, referralsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/companies`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/sponsors/list`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/sponsors/expiring?days=14`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/sponsors/tiers`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/email-queue/stats`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/email-queue?limit=20`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/users?limit=100`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/promo-users`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/catalog-reminders/pending`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/admin/referrals`, { headers: getAuthHeaders() })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (companiesRes.ok) setCompanies(await companiesRes.json());
      if (sponsorsRes.ok) setSponsors(await sponsorsRes.json());
      if (expiringRes.ok) setExpiringSoon(await expiringRes.json());
      if (tiersRes.ok) setTierPricing(await tiersRes.json());
      if (emailStatsRes.ok) setEmailStats(await emailStatsRes.json());
      if (emailQueueRes.ok) setEmailQueue(await emailQueueRes.json());
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        // Handle both array format and {summary, users} format
        setUsers(Array.isArray(usersData) ? usersData : (usersData.users || []));
      }
      if (promoUsersRes.ok) setPromoUsers(await promoUsersRes.json());
      if (remindersRes.ok) setCatalogReminders(await remindersRes.json());
      if (referralsRes.ok) setReferralData(await referralsRes.json());
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/sponsors/history`, { headers: getAuthHeaders() });
      if (res.ok) setSponsorHistory(await res.json());
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  // Fetch user details
  const fetchUserDetail = async (userId) => {
    setUserDetailDialog({ open: true, user: null, loading: true, data: null });
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setUserDetailDialog({ open: true, user: data.user, loading: false, data });
      } else {
        toast.error('Error al cargar datos del usuario');
        setUserDetailDialog({ open: false, user: null, loading: false, data: null });
      }
    } catch (error) {
      console.error('Error fetching user detail:', error);
      toast.error('Error de conexión');
      setUserDetailDialog({ open: false, user: null, loading: false, data: null });
    }
  };

  // Fetch emails with optional filter
  const fetchEmails = async (status = 'all') => {
    try {
      const url = status === 'all' 
        ? `${API_URL}/api/admin/email-queue?limit=50` 
        : `${API_URL}/api/admin/email-queue?status=${status}&limit=50`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) setEmailQueue(await res.json());
    } catch (error) {
      console.error('Error fetching emails:', error);
    }
  };

  // Handle email filter change
  const handleEmailFilterChange = (filter) => {
    setEmailFilter(filter);
    fetchEmails(filter);
  };

  // Send catalog reminders
  const handleSendCatalogReminders = async () => {
    if (catalogReminders.pending_count === 0) {
      toast.info('No hay usuarios pendientes de recordatorio');
      return;
    }
    
    setSendingReminders(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/catalog-reminders/send`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        // Refresh the pending list
        const remindersRes = await fetch(`${API_URL}/api/admin/catalog-reminders/pending`, { headers: getAuthHeaders() });
        if (remindersRes.ok) setCatalogReminders(await remindersRes.json());
      } else {
        toast.error(data.detail || 'Error al enviar recordatorios');
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      toast.error('Error de conexión');
    } finally {
      setSendingReminders(false);
    }
  };

  // Create user function
  const handleCreateUser = async () => {
    if (!newUserForm.email || !newUserForm.name || !newUserForm.password) {
      toast.error('Todos los campos son requeridos');
      return;
    }
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/create`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newUserForm)
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Usuario ${data.email} creado exitosamente`);
        setCreateUserDialog(false);
        setNewUserForm({ email: '', name: '', password: '', subscription_plan: 'free', apply_launch_promo: false });
        fetchData();
      } else {
        toast.error(data.detail || 'Error al crear usuario');
      }
    } catch (error) {
      toast.error('Error al crear usuario');
    } finally {
      setProcessing(false);
    }
  };

  // Send promo expiration emails
  const handleSendPromoEmails = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/send-promo-expiration-emails`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Emails enviados: ${data.sent.expiring_soon} próximos a expirar, ${data.sent.expired} expirados`);
        if (data.sent.errors?.length > 0) {
          console.warn('Email errors:', data.sent.errors);
        }
      } else {
        toast.error(data.detail || 'Error al enviar emails');
      }
    } catch (error) {
      toast.error('Error al enviar emails de promoción');
    } finally {
      setProcessing(false);
    }
  };

  // Update user subscription
  const handleUpdateSubscription = async (userId, planId, applyPromo = false) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/subscription`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ plan_id: planId, apply_launch_promo: applyPromo })
      });
      if (res.ok) {
        toast.success('Suscripción actualizada');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Error al actualizar');
      }
    } catch (error) {
      toast.error('Error al actualizar suscripción');
    }
  };

  const handleVerifyCompany = async (companyId, verified) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/companies/${companyId}/verify`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ verified })
      });
      if (res.ok) {
        toast.success(verified ? 'Empresa verificada' : 'Verificación removida');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Error al verificar');
      }
    } catch (error) {
      toast.error('Error al actualizar empresa');
    }
  };

  // Delete user function
  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`¿Estás seguro de eliminar al usuario ${userEmail}? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Usuario eliminado');
        fetchData();
      } else {
        toast.error(data.detail || 'Error al eliminar usuario');
      }
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  // Delete company function
  const handleHideCompany = async (companyId, hidden) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/companies/${companyId}/hide`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden, reason: 'admin' })
      });
      if (res.ok) {
        toast.success(hidden ? 'Empresa oculta del directorio' : 'Empresa restaurada');
        fetchData();
      } else {
        toast.error('No se pudo actualizar la empresa');
      }
    } catch (e) {
      toast.error('Error de conexión');
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (!window.confirm(`¿Estás seguro de eliminar la empresa "${companyName}"? Esto también eliminará todos sus productos. Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/companies/${companyId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Empresa eliminada');
        fetchData();
      } else {
        toast.error(data.detail || 'Error al eliminar empresa');
      }
    } catch (error) {
      toast.error('Error al eliminar empresa');
    }
  };

  const handleSetSponsor = async () => {
    if (!sponsorDialog.company) return;
    setProcessing(true);
    try {
      await fetch(`${API_URL}/api/admin/sponsors/${sponsorDialog.company.company_id}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(sponsorForm)
      });
      toast.success('Patrocinio asignado');
      setSponsorDialog({ open: false, company: null, mode: 'create' });
      fetchData();
    } catch (error) {
      toast.error('Error al asignar patrocinio');
    } finally {
      setProcessing(false);
    }
  };

  const handleExtendSponsor = async () => {
    if (!extendDialog.sponsor) return;
    setProcessing(true);
    try {
      await fetch(`${API_URL}/api/admin/sponsors/${extendDialog.sponsor.company_id}/extend`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ days: extendDays })
      });
      toast.success(`Patrocinio extendido ${extendDays} días`);
      setExtendDialog({ open: false, sponsor: null });
      fetchData();
    } catch (error) {
      toast.error('Error al extender patrocinio');
    } finally {
      setProcessing(false);
    }
  };

  const handleChangeTier = async (companyId, newTier) => {
    try {
      await fetch(`${API_URL}/api/admin/sponsors/${companyId}/tier`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ tier: newTier })
      });
      toast.success('Tier actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al cambiar tier');
    }
  };

  const handleRemoveSponsor = async (companyId) => {
    if (!confirm('¿Seguro que deseas remover este patrocinio?')) return;
    try {
      await fetch(`${API_URL}/api/admin/sponsors/${companyId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      toast.success('Patrocinio removido');
      fetchData();
    } catch (error) {
      toast.error('Error al remover patrocinio');
    }
  };

  const handleProcessEmails = async () => {
    setProcessingEmails(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/email-queue/process`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(`Enviados: ${result.sent}, Fallidos: ${result.failed}`);
        fetchData();
      } else {
        toast.error(result.detail || 'Error al procesar emails');
      }
    } catch (error) {
      toast.error('Error al procesar cola de emails');
    } finally {
      setProcessingEmails(false);
    }
  };

  const hiddenCompaniesCount = companies.filter(c => c.hide_from_directory).length;
  const filteredCompanies = companies.filter(c => 
    (showHiddenCompanies ? c.hide_from_directory : !c.hide_from_directory) &&
    (c.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getDaysColor = (days) => {
    if (days <= 3) return 'text-red-600';
    if (days <= 7) return 'text-orange-500';
    if (days <= 14) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center p-8">
          <XCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Acceso denegado</h2>
          <p className="text-slate-500">Solo administradores pueden acceder a esta sección</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div data-testid="admin-panel">
      <div className="page-header">
        <h1 className="page-title flex items-center gap-2">
          <BarChart3 className="w-6 h-6" style={{ color: '#1E4C8E' }} />
          Panel de Administración
        </h1>
        <p className="page-subtitle">Gestiona la plataforma TexHub</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Empresas</p>
                <p className="text-2xl font-bold">{stats?.total_companies || 0}</p>
              </div>
              <Building2 className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Usuarios</p>
                <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
              </div>
              <Users className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Productos</p>
                <p className="text-2xl font-bold">{stats?.total_products || 0}</p>
              </div>
              <Package className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Patrocinadores</p>
                <p className="text-2xl font-bold">{sponsors.length}</p>
              </div>
              <Megaphone className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expiring Soon Alert */}
      {expiringSoon.length > 0 && (
        <Card className="mb-6 border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <div className="flex-1">
                <p className="font-medium text-orange-800">
                  {expiringSoon.length} patrocinio{expiringSoon.length > 1 ? 's' : ''} por vencer en los próximos 14 días
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {expiringSoon.map(s => (
                    <Badge key={s.company_id} variant="outline" className="border-orange-300 text-orange-700">
                      {s.company_name} ({s.days_remaining} días)
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Custom Tabs */}
      <div className="mb-6">
        <div className="inline-flex h-10 items-center justify-center rounded-lg bg-slate-100 p-1 text-slate-500">
          <button
            onClick={() => setActiveTab('companies')}
            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
              activeTab === 'companies' ? 'bg-white text-slate-900 shadow-sm' : 'hover:bg-white/50'
            }`}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Empresas ({companies.length})
          </button>
          <button
            onClick={() => setActiveTab('sponsors')}
            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
              activeTab === 'sponsors' ? 'bg-white text-slate-900 shadow-sm' : 'hover:bg-white/50'
            }`}
          >
            <Megaphone className="w-4 h-4 mr-2" />
            Patrocinadores ({sponsors.length})
          </button>
          <button
            onClick={() => setActiveTab('emails')}
            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
              activeTab === 'emails' ? 'bg-white text-slate-900 shadow-sm' : 'hover:bg-white/50'
            }`}
          >
            <Mail className="w-4 h-4 mr-2" />
            Emails ({emailStats?.pending || 0})
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
              activeTab === 'users' ? 'bg-white text-slate-900 shadow-sm' : 'hover:bg-white/50'
            }`}
          >
            <Users className="w-4 h-4 mr-2" />
            Usuarios ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('reminders')}
            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
              activeTab === 'reminders' ? 'bg-white text-slate-900 shadow-sm' : 'hover:bg-white/50'
            }`}
          >
            <Clock className="w-4 h-4 mr-2" />
            Recordatorios ({catalogReminders.pending_count})
          </button>
          <button
            onClick={() => setActiveTab('referrals')}
            className={`inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
              activeTab === 'referrals' ? 'bg-white text-slate-900 shadow-sm' : 'hover:bg-white/50'
            }`}
          >
            <Gift className="w-4 h-4 mr-2" />
            Referidos ({referralData.summary?.total_referrals || 0})
          </button>
        </div>
      </div>

      {/* Tab Content: Companies */}
      {activeTab === 'companies' && (
        <div>
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar empresa..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="admin-company-search"
              />
            </div>
            <Button
              variant={showHiddenCompanies ? 'default' : 'outline'}
              onClick={() => setShowHiddenCompanies(v => !v)}
              className={showHiddenCompanies ? 'bg-slate-800 hover:bg-slate-900' : ''}
              data-testid="admin-toggle-hidden-companies"
            >
              <EyeOff className="w-4 h-4 mr-2" />
              {showHiddenCompanies ? 'Ver visibles' : `Ver ocultas (${hiddenCompaniesCount})`}
            </Button>
          </div>
          {showHiddenCompanies && (
            <p className="text-sm text-slate-500 mb-3" data-testid="admin-hidden-companies-note">
              Empresas ocultas del directorio, búsqueda y conteos públicos (datos demo/prueba). Nada se borra: puedes restaurarlas cuando quieras.
            </p>
          )}

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-semibold">Empresa</th>
                      <th className="text-left p-4 font-semibold">Tipo</th>
                      <th className="text-left p-4 font-semibold">Estado</th>
                      <th className="text-left p-4 font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompanies.map((company) => (
                      <tr key={company.company_id} className="border-b hover:bg-slate-50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-slate-400" />
                            </div>
                            <div>
                              <button 
                                onClick={() => window.open(`/company/${company.company_id}`, '_blank')}
                                className="font-medium text-left hover:text-emerald-600 hover:underline cursor-pointer"
                              >
                                {company.company_name}
                              </button>
                              <p className="text-xs text-slate-500">{company.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline">{company.company_type}</Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {company.verified ? (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Verificada
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-slate-500">Pendiente</Badge>
                            )}
                            {company.is_sponsored && (
                              <Badge className={`${TIER_CONFIG[company.sponsor_tier]?.bg} ${TIER_CONFIG[company.sponsor_tier]?.color}`}>
                                {TIER_CONFIG[company.sponsor_tier]?.icon} {company.sponsor_tier}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {company.hide_from_directory ? (
                              <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700"
                                onClick={() => handleHideCompany(company.company_id, false)}
                                data-testid={`admin-restore-company-${company.company_id}`}>
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Restaurar
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" className="text-slate-500"
                                title="Ocultar del directorio (no borra)"
                                onClick={() => handleHideCompany(company.company_id, true)}
                                data-testid={`admin-hide-company-${company.company_id}`}>
                                <EyeOff className="w-4 h-4" />
                              </Button>
                            )}
                            {!company.verified ? (
                              <Button size="sm" onClick={() => handleVerifyCompany(company.company_id, true)}
                                style={{ backgroundColor: '#34B772' }}>
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Verificar
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline"
                                onClick={() => handleVerifyCompany(company.company_id, false)}>
                                Quitar
                              </Button>
                            )}
                            {!company.is_sponsored && (
                              <Button size="sm" variant="outline"
                                onClick={() => {
                                  setSponsorDialog({ open: true, company, mode: 'create' });
                                  setSponsorForm({ tier: 'bronze', days: 30 });
                                }}>
                                <Crown className="w-4 h-4 mr-1" />
                                Sponsor
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2"
                              onClick={() => handleDeleteCompany(company.company_id, company.name)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab Content: Sponsors */}
      {activeTab === 'sponsors' && (
        <div>
          {/* Tier Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {Object.entries(tierPricing).map(([key, tier]) => (
              <Card key={key} className={`${TIER_CONFIG[key]?.border} border-2`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">{TIER_CONFIG[key]?.icon}</span>
                    <h3 className="font-bold text-lg">{tier.name}</h3>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="font-semibold text-slate-900">${tier.price_monthly?.toLocaleString()} MXN/mes</p>
                    <p className="text-slate-500">${tier.price_quarterly?.toLocaleString()} trimestral</p>
                    <p className="text-slate-500">${tier.price_yearly?.toLocaleString()} anual</p>
                  </div>
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-slate-500 mb-2">Beneficios:</p>
                    <ul className="text-xs space-y-1">
                      {tier.benefits?.map((b, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Actions Bar */}
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-600">{sponsors.length} patrocinadores activos</p>
            <Button variant="outline" size="sm" onClick={() => { fetchHistory(); setHistoryDialog({ open: true }); }}>
              <History className="w-4 h-4 mr-2" />
              Ver historial
            </Button>
          </div>

          {sponsors.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Megaphone className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="font-semibold text-slate-700 mb-2">No hay patrocinadores</h3>
                <p className="text-slate-500 text-sm">Asigna patrocinios desde la pestaña de empresas</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {sponsors.map((sponsor) => {
                const tierConfig = TIER_CONFIG[sponsor.sponsor_tier] || TIER_CONFIG.bronze;
                const daysColor = getDaysColor(sponsor.days_remaining);
                
                return (
                  <Card key={sponsor.company_id} className={`${tierConfig.border} border-2`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* Company Info */}
                        <div className="flex items-center gap-4 flex-1">
                          <div className={`w-14 h-14 ${tierConfig.bg} rounded-xl flex items-center justify-center`}>
                            <Crown className={`w-7 h-7 ${tierConfig.color}`} />
                          </div>
                          <div>
                            <button 
                              onClick={() => window.open(`/company/${sponsor.company_id}`, '_blank')}
                              className="font-semibold text-lg text-left hover:text-emerald-600 hover:underline cursor-pointer"
                            >
                              {sponsor.company_name}
                            </button>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                              <Badge className={`${tierConfig.bg} ${tierConfig.color} border-0`}>
                                {tierConfig.icon} {sponsor.sponsor_tier?.toUpperCase()}
                              </Badge>
                              <span className={`flex items-center gap-1 ${daysColor}`}>
                                <Clock className="w-4 h-4" />
                                {sponsor.days_remaining} días restantes
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Metrics */}
                        <div className="grid grid-cols-4 gap-4 text-center">
                          <div>
                            <p className="text-lg font-bold text-slate-900">{sponsor.metrics?.profile_views || 0}</p>
                            <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
                              <Eye className="w-3 h-3" /> Vistas
                            </p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-slate-900">{sponsor.metrics?.product_views || 0}</p>
                            <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
                              <Package className="w-3 h-3" /> Productos
                            </p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-slate-900">{sponsor.metrics?.quotes_received || 0}</p>
                            <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
                              <FileText className="w-3 h-3" /> Cotizaciones
                            </p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-slate-900">{sponsor.metrics?.orders_received || 0}</p>
                            <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
                              <ShoppingCart className="w-3 h-3" /> Pedidos
                            </p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Select value={sponsor.sponsor_tier} onValueChange={(v) => handleChangeTier(sponsor.company_id, v)}>
                            <SelectTrigger className="w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="gold">🥇 Gold</SelectItem>
                              <SelectItem value="silver">🥈 Silver</SelectItem>
                              <SelectItem value="bronze">🥉 Bronze</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="outline" onClick={() => {
                            setExtendDialog({ open: true, sponsor });
                            setExtendDays(30);
                          }}>
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600"
                            onClick={() => handleRemoveSponsor(sponsor.company_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Progress bar for days remaining */}
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span>Vence: {sponsor.sponsor_expires_at ? new Date(sponsor.sponsor_expires_at).toLocaleDateString('es-MX') : 'N/A'}</span>
                          <span>{Math.min(100, Math.round((sponsor.days_remaining || 0) / 30 * 100))}%</span>
                        </div>
                        <Progress value={Math.min(100, Math.round((sponsor.days_remaining || 0) / 30 * 100))} className="h-2" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab Content: Emails */}
      {activeTab === 'emails' && (
        <div>
          {/* Clickable Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${emailFilter === 'pending' ? 'ring-2 ring-yellow-400' : 'bg-yellow-50 border-yellow-200'}`}
              onClick={() => handleEmailFilterChange('pending')}
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-700">{emailStats?.pending || 0}</p>
                <p className="text-sm text-yellow-600">Pendientes</p>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${emailFilter === 'sent' ? 'ring-2 ring-green-400' : 'bg-green-50 border-green-200'}`}
              onClick={() => handleEmailFilterChange('sent')}
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{emailStats?.sent || 0}</p>
                <p className="text-sm text-green-600">Enviados</p>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${emailFilter === 'failed' ? 'ring-2 ring-red-400' : 'bg-red-50 border-red-200'}`}
              onClick={() => handleEmailFilterChange('failed')}
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-700">{emailStats?.failed || 0}</p>
                <p className="text-sm text-red-600">Fallidos</p>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${emailFilter === 'all' ? 'ring-2 ring-slate-400' : 'bg-slate-50 border-slate-200'}`}
              onClick={() => handleEmailFilterChange('all')}
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-700">{emailStats?.total || 0}</p>
                <p className="text-sm text-slate-600">Total</p>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-600">
              {emailFilter === 'all' ? 'Todos los emails' : 
               emailFilter === 'pending' ? 'Emails pendientes' :
               emailFilter === 'sent' ? 'Emails enviados' : 'Emails fallidos'}
              {' '}({emailQueue.length})
            </p>
            <Button onClick={handleProcessEmails} disabled={processingEmails || (emailStats?.pending || 0) === 0}
              style={{ backgroundColor: '#34B772' }}>
              {processingEmails ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Enviar ({emailStats?.pending || 0})</>
              )}
            </Button>
          </div>

          {emailQueue.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="font-semibold text-slate-700 mb-2">No hay emails en cola</h3>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {emailQueue.map((email) => (
                <Card 
                  key={email.email_id} 
                  className={`cursor-pointer hover:shadow-md transition-shadow ${
                    email.status === 'pending' ? 'border-yellow-200 bg-yellow-50/30' :
                    email.status === 'sent' ? 'border-green-200 bg-green-50/30' : 'border-red-200 bg-red-50/30'
                  }`}
                  onClick={() => setSelectedEmail(email)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge className={
                            email.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            email.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }>
                            {email.status === 'pending' ? 'Pendiente' : email.status === 'sent' ? 'Enviado' : 'Fallido'}
                          </Badge>
                          <span className="text-xs text-slate-400">Clic para ver detalles</span>
                        </div>
                        <p className="font-medium text-sm mt-1 truncate">{email.subject}</p>
                        <p className="text-xs text-slate-500">Para: {email.to_email}</p>
                      </div>
                      <div className="text-xs text-slate-400">
                        {email.created_at ? new Date(email.created_at).toLocaleDateString('es-MX') : 'N/A'}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Email Detail Dialog */}
          <Dialog open={!!selectedEmail} onOpenChange={() => setSelectedEmail(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Detalle del Email
                </DialogTitle>
              </DialogHeader>
              {selectedEmail && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge className={
                      selectedEmail.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      selectedEmail.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }>
                      {selectedEmail.status === 'pending' ? 'Pendiente' : selectedEmail.status === 'sent' ? 'Enviado' : 'Fallido'}
                    </Badge>
                    <span className="text-sm text-slate-500">
                      {selectedEmail.created_at ? new Date(selectedEmail.created_at).toLocaleString('es-MX') : 'N/A'}
                    </span>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-medium">Para</p>
                      <p className="text-sm font-medium">{selectedEmail.to_email}</p>
                      {selectedEmail.to_name && (
                        <p className="text-xs text-slate-500">{selectedEmail.to_name}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-medium">Asunto</p>
                      <p className="text-sm font-medium">{selectedEmail.subject}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase font-medium">Mensaje</p>
                      <p className="text-sm whitespace-pre-wrap">{selectedEmail.body}</p>
                    </div>
                    {selectedEmail.link && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase font-medium">Link</p>
                        <a 
                          href={selectedEmail.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-emerald-600 hover:underline"
                        >
                          {selectedEmail.link}
                        </a>
                      </div>
                    )}
                    {selectedEmail.error && (
                      <div>
                        <p className="text-xs text-red-500 uppercase font-medium">Error</p>
                        <p className="text-sm text-red-600">{selectedEmail.error}</p>
                      </div>
                    )}
                    {selectedEmail.sent_at && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase font-medium">Enviado el</p>
                        <p className="text-sm">{new Date(selectedEmail.sent_at).toLocaleString('es-MX')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Tab Content: Users */}
      {activeTab === 'users' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2">
              <Button onClick={() => setCreateUserDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Crear Usuario
              </Button>
              <Button onClick={handleSendPromoEmails} disabled={processing} variant="outline">
                {processing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" />Enviar Emails Promoción</>
                )}
              </Button>
            </div>
          </div>

          {/* Promo Users Section */}
          {promoUsers.length > 0 && (
            <Card className="mb-6 border-purple-200 bg-purple-50/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-purple-900">Usuarios con Promoción Premium ({promoUsers.length})</h3>
                </div>
                <div className="grid gap-2">
                  {promoUsers.map((u) => (
                    <div key={u.user_id} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                      <div>
                        <p className="font-medium">{u.name || u.email}</p>
                        <p className="text-xs text-slate-500">{u.email}</p>
                      </div>
                      <div className="text-right">
                        {u.is_expired ? (
                          <Badge className="bg-slate-100 text-slate-600">Expirada</Badge>
                        ) : (
                          <Badge className="bg-purple-100 text-purple-700">
                            {u.days_remaining} días restantes
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* All Users Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Usuario</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Plan</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Rol</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Creado</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(users || []).map((u) => (
                      <tr key={u.user_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => fetchUserDetail(u.user_id)}>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium hover:text-emerald-600">
                              {u.name || 'Sin nombre'}
                            </p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                            {u.company_id && (
                              <p className="text-xs text-emerald-600">Empresa: {u.company_id.slice(0, 15)}...</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={
                            u.subscription_plan === 'pro' ? 'bg-emerald-100 text-emerald-700' :
                            'bg-slate-100 text-slate-600'
                          }>
                            {u.subscription_plan === 'pro' ? 'PRO' : 'Free'}
                          </Badge>
                          {u.ai_images_purchased > 0 && (
                            <span className="text-xs text-slate-400 ml-2">
                              AI: {u.ai_images_purchased}
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{u.role || 'user'}</Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX') : 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2 justify-end">
                            <Select
                              value={u.subscription_plan || 'free'}
                              onValueChange={(val) => handleUpdateSubscription(u.user_id, val)}
                            >
                              <SelectTrigger className="w-28 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="free">Free</SelectItem>
                                <SelectItem value="pro">PRO</SelectItem>
                              </SelectContent>
                            </Select>
                            {u.role !== 'admin' && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteUser(u.user_id, u.email)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab Content: Catalog Reminders */}
      {activeTab === 'reminders' && (
        <div className="space-y-6">
          {/* Config Info */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-lg">Recordatorios de Catálogo</h3>
                  <p className="text-sm text-slate-500">
                    Envía correos automáticos a usuarios que no han subido suficientes productos
                  </p>
                </div>
                <Button
                  onClick={handleSendCatalogReminders}
                  disabled={sendingReminders || catalogReminders.pending_count === 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {sendingReminders ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar Recordatorios ({catalogReminders.pending_count})
                    </>
                  )}
                </Button>
              </div>
              
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-700">{catalogReminders.config?.interval_days || 7}</p>
                  <p className="text-xs text-slate-500">días entre recordatorios</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-700">{catalogReminders.config?.max_reminders || 3}</p>
                  <p className="text-xs text-slate-500">máximo de correos</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-700">&lt; {catalogReminders.config?.min_products || 3}</p>
                  <p className="text-xs text-slate-500">productos = recordatorio</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Users */}
          <Card>
            <CardContent className="pt-6">
              <h4 className="font-medium mb-4">
                Usuarios pendientes de recordatorio ({catalogReminders.pending_count})
              </h4>
              
              {catalogReminders.users?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                  <p>No hay usuarios pendientes de recordatorio</p>
                  <p className="text-xs mt-1">Todos tienen suficientes productos o ya recibieron sus 3 recordatorios</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left py-2 px-4 text-xs font-medium text-slate-500">Usuario</th>
                        <th className="text-left py-2 px-4 text-xs font-medium text-slate-500">Productos</th>
                        <th className="text-left py-2 px-4 text-xs font-medium text-slate-500">Recordatorios enviados</th>
                        <th className="text-left py-2 px-4 text-xs font-medium text-slate-500">Días desde último</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {catalogReminders.users?.map((u, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-sm">{u.name || 'Sin nombre'}</p>
                              <p className="text-xs text-slate-500">{u.email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant={u.product_count === 0 ? 'destructive' : 'outline'}>
                              {u.product_count} productos
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {u.reminders_sent} / {catalogReminders.config?.max_reminders || 3}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {u.days_since_last_reminder !== null 
                              ? `${u.days_since_last_reminder} días` 
                              : 'Nunca'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab Content: Referrals */}
      {activeTab === 'referrals' && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-2xl font-bold text-slate-700">{referralData.summary?.total_referrers || 0}</p>
                <p className="text-xs text-slate-500">Usuarios con referidos</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{referralData.summary?.total_referrals || 0}</p>
                <p className="text-xs text-slate-500">Total referidos</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{referralData.summary?.users_featured || 0}</p>
                <p className="text-xs text-slate-500">Perfiles destacados</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{referralData.summary?.users_with_pro_bonus || 0}</p>
                <p className="text-xs text-slate-500">Con días PRO gratis</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{referralData.summary?.total_users_referred || 0}</p>
                <p className="text-xs text-slate-500">Registrados por referido</p>
              </CardContent>
            </Card>
          </div>

          {/* Referrers Table */}
          <Card>
            <CardContent className="pt-6">
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <Gift className="w-4 h-4 text-emerald-600" />
                Usuarios con Referidos
              </h4>
              
              {referralData.users?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Gift className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Aún no hay usuarios con referidos</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left py-2 px-4 text-xs font-medium text-slate-500">Usuario</th>
                        <th className="text-left py-2 px-4 text-xs font-medium text-slate-500">Código</th>
                        <th className="text-left py-2 px-4 text-xs font-medium text-slate-500">Referidos</th>
                        <th className="text-left py-2 px-4 text-xs font-medium text-slate-500">Beneficios</th>
                        <th className="text-left py-2 px-4 text-xs font-medium text-slate-500">PRO Expira</th>
                        <th className="text-left py-2 px-4 text-xs font-medium text-slate-500">Stripe</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {referralData.users?.map((u, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-sm">{u.name || 'Sin nombre'}</p>
                              <p className="text-xs text-slate-500">{u.email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <code className="text-xs bg-slate-100 px-2 py-1 rounded">{u.referral_code}</code>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                              {u.referrals_count} referidos
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap gap-1">
                              {u.is_featured && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">Destacado</Badge>
                              )}
                              {u.referrals_count >= 5 && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">+{Math.floor(u.referrals_count / 5) * 30}d PRO</Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {u.pro_expires_at ? (
                              <span className="text-purple-600">
                                {new Date(u.pro_expires_at).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {u.stripe_subscription_id ? (
                              <div className="space-y-1">
                                <Badge variant={u.stripe_paused ? 'secondary' : 'outline'} className={u.stripe_paused ? 'bg-yellow-100 text-yellow-700' : ''}>
                                  {u.stripe_paused ? 'Pausado' : u.stripe_status || 'Activo'}
                                </Badge>
                                {u.stripe_paused && u.stripe_resumes_at && (
                                  <p className="text-xs text-yellow-600">
                                    Reactiva: {new Date(u.stripe_resumes_at * 1000).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">Sin suscripción</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Referrals Detail */}
          {referralData.users?.length > 0 && (
            <Card>
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-4">Detalle de Referidos por Usuario</h4>
                <div className="space-y-4">
                  {referralData.users?.slice(0, 5).map((u, i) => (
                    u.referrals?.length > 0 && (
                      <div key={i} className="border rounded-lg p-3">
                        <p className="font-medium text-sm mb-2">{u.name} ({u.referral_code})</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {u.referrals.map((ref, j) => (
                            <div key={j} className="text-xs bg-slate-50 rounded p-2">
                              <p className="font-medium">{ref.name}</p>
                              <p className="text-slate-500">{ref.email}</p>
                              <p className="text-slate-400">{new Date(ref.created_at).toLocaleDateString()}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Create User Dialog */}
      <Dialog open={createUserDialog} onOpenChange={setCreateUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Nuevo Usuario</DialogTitle>
            <DialogDescription>Crea un usuario manualmente y envía las credenciales por email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre completo</Label>
              <Input 
                value={newUserForm.name} 
                onChange={(e) => setNewUserForm({...newUserForm, name: e.target.value})}
                placeholder="Juan Pérez"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input 
                type="email"
                value={newUserForm.email} 
                onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                placeholder="usuario@ejemplo.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Contraseña</Label>
              <Input 
                value={newUserForm.password} 
                onChange={(e) => setNewUserForm({...newUserForm, password: e.target.value})}
                placeholder="Contraseña segura"
              />
            </div>
            <div className="space-y-2">
              <Label>Plan de suscripción</Label>
              <Select 
                value={newUserForm.subscription_plan} 
                onValueChange={(v) => setNewUserForm({...newUserForm, subscription_plan: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free (Gratis)</SelectItem>
                  <SelectItem value="pro">PRO ($599 MXN/mes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateUser} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700">
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear Usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sponsor Dialog */}
      <Dialog open={sponsorDialog.open} onOpenChange={(open) => setSponsorDialog({ ...sponsorDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignar Patrocinio</DialogTitle>
            <DialogDescription>Configura el patrocinio para {sponsorDialog.company?.company_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nivel de patrocinio</Label>
              <Select value={sponsorForm.tier} onValueChange={(v) => setSponsorForm({...sponsorForm, tier: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gold">🥇 Gold - ${tierPricing.gold?.price_monthly?.toLocaleString()}/mes</SelectItem>
                  <SelectItem value="silver">🥈 Silver - ${tierPricing.silver?.price_monthly?.toLocaleString()}/mes</SelectItem>
                  <SelectItem value="bronze">🥉 Bronze - ${tierPricing.bronze?.price_monthly?.toLocaleString()}/mes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Duración</Label>
              <Select value={sponsorForm.days.toString()} onValueChange={(v) => setSponsorForm({...sponsorForm, days: parseInt(v)})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 días (1 mes)</SelectItem>
                  <SelectItem value="90">90 días (3 meses)</SelectItem>
                  <SelectItem value="180">180 días (6 meses)</SelectItem>
                  <SelectItem value="365">365 días (1 año)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSponsorDialog({ open: false, company: null, mode: 'create' })}>
              Cancelar
            </Button>
            <Button onClick={handleSetSponsor} disabled={processing} style={{ backgroundColor: '#34B772' }}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Asignar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Dialog */}
      <Dialog open={extendDialog.open} onOpenChange={(open) => setExtendDialog({ ...extendDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Extender Patrocinio</DialogTitle>
            <DialogDescription>Añadir días al patrocinio de {extendDialog.sponsor?.company_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Días a añadir</Label>
              <Select value={extendDays.toString()} onValueChange={(v) => setExtendDays(parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 días</SelectItem>
                  <SelectItem value="14">14 días</SelectItem>
                  <SelectItem value="30">30 días</SelectItem>
                  <SelectItem value="60">60 días</SelectItem>
                  <SelectItem value="90">90 días</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-slate-500">
              Vencimiento actual: {extendDialog.sponsor?.sponsor_expires_at && 
                new Date(extendDialog.sponsor.sponsor_expires_at).toLocaleDateString('es-MX')}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendDialog({ open: false, sponsor: null })}>Cancelar</Button>
            <Button onClick={handleExtendSponsor} disabled={processing} style={{ backgroundColor: '#34B772' }}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : `Extender ${extendDays} días`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialog.open} onOpenChange={(open) => setHistoryDialog({ open })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Historial de Patrocinios</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {sponsorHistory.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No hay historial</p>
            ) : (
              <div className="space-y-2">
                {sponsorHistory.map((h) => (
                  <div key={h.history_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      h.action === 'created' ? 'bg-green-100' :
                      h.action === 'extended' ? 'bg-blue-100' :
                      h.action === 'tier_changed' ? 'bg-purple-100' : 'bg-red-100'
                    }`}>
                      {h.action === 'created' ? <Plus className="w-4 h-4 text-green-600" /> :
                       h.action === 'extended' ? <RefreshCw className="w-4 h-4 text-blue-600" /> :
                       h.action === 'tier_changed' ? <TrendingUp className="w-4 h-4 text-purple-600" /> :
                       <Trash2 className="w-4 h-4 text-red-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{h.company_name}</p>
                      <p className="text-xs text-slate-500">
                        {h.action === 'created' ? `Patrocinio ${h.tier} creado (${h.days} días)` :
                         h.action === 'extended' ? `Extendido ${h.extended_days} días` :
                         h.action === 'tier_changed' ? `Tier: ${h.old_tier} → ${h.new_tier}` :
                         'Patrocinio removido'}
                      </p>
                    </div>
                    <p className="text-xs text-slate-400">
                      {h.created_at ? new Date(h.created_at).toLocaleDateString('es-MX') : 'N/A'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={userDetailDialog.open} onOpenChange={(open) => !open && setUserDetailDialog({ open: false, user: null, loading: false, data: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Detalles del Usuario
            </DialogTitle>
          </DialogHeader>
          
          {userDetailDialog.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : userDetailDialog.data ? (
            <div className="space-y-6">
              {/* User Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Información Personal
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Nombre:</span>
                        <span className="font-medium">{userDetailDialog.data.user?.name || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Email:</span>
                        <span className="font-medium">{userDetailDialog.data.user?.email || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Teléfono:</span>
                        <span className="font-medium">{userDetailDialog.data.user?.phone || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Plan:</span>
                        <Badge className={userDetailDialog.data.user?.subscription_plan === 'pro' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'}>
                          {userDetailDialog.data.user?.subscription_plan || 'free'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Tipo:</span>
                        <Badge className={userDetailDialog.data.user?.account_type === 'buyer' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                          {userDetailDialog.data.user?.account_type === 'buyer' ? 'Comprador' : 'Vendedor'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Registro:</span>
                        <span className="font-medium">
                          {userDetailDialog.data.user?.created_at 
                            ? new Date(userDetailDialog.data.user.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                            : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Onboarding:</span>
                        {userDetailDialog.data.user?.onboarding_completed ? (
                          <Badge className="bg-green-100 text-green-700">Completado</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">Pendiente</Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Empresa
                    </h3>
                    {userDetailDialog.data.company ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Nombre:</span>
                          <span className="font-medium">{userDetailDialog.data.company.company_name || userDetailDialog.data.company.name || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Teléfono:</span>
                          <span className="font-medium">{userDetailDialog.data.company.phone || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Ciudad:</span>
                          <span className="font-medium">{userDetailDialog.data.company.location_city || userDetailDialog.data.company.city || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Estado:</span>
                          <span className="font-medium">{userDetailDialog.data.company.location_state || userDetailDialog.data.company.state || '-'}</span>
                        </div>
                        
                        {/* Categorías de negocio */}
                        <div className="pt-2 border-t mt-2">
                          <span className="text-slate-500 text-xs">Categorías:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {userDetailDialog.data.company.is_proveedor && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Proveedor</Badge>
                            )}
                            {userDetailDialog.data.company.is_maquilero && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">Maquilador</Badge>
                            )}
                            {userDetailDialog.data.company.is_comprador && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs">Comprador</Badge>
                            )}
                            {userDetailDialog.data.company.comprador_fabricante && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Fabricante</Badge>
                            )}
                            {userDetailDialog.data.company.comprador_tienda && (
                              <Badge className="bg-pink-100 text-pink-700 text-xs">Tienda</Badge>
                            )}
                            {userDetailDialog.data.company.comprador_marca && (
                              <Badge className="bg-indigo-100 text-indigo-700 text-xs">Marca</Badge>
                            )}
                            {!userDetailDialog.data.company.is_proveedor && 
                             !userDetailDialog.data.company.is_maquilero && 
                             !userDetailDialog.data.company.is_comprador && (
                              <span className="text-slate-400 text-xs italic">Sin categoría</span>
                            )}
                          </div>
                        </div>

                        {/* Subcategorías de proveedor */}
                        {userDetailDialog.data.company.is_proveedor && (
                          <div className="text-xs">
                            <span className="text-slate-400">Provee: </span>
                            {[
                              userDetailDialog.data.company.proveedor_telas && 'Telas',
                              userDetailDialog.data.company.proveedor_avios && 'Avíos',
                              userDetailDialog.data.company.proveedor_etiquetas && 'Etiquetas',
                              userDetailDialog.data.company.proveedor_hilos && 'Hilos',
                              userDetailDialog.data.company.proveedor_empaque && 'Empaque',
                              userDetailDialog.data.company.proveedor_otros && 'Otros',
                            ].filter(Boolean).join(', ') || 'No especificado'}
                          </div>
                        )}

                        {/* Subcategorías de maquilador */}
                        {userDetailDialog.data.company.is_maquilero && (
                          <div className="text-xs">
                            <span className="text-slate-400">Servicios: </span>
                            {[
                              userDetailDialog.data.company.maquilero_corte && 'Corte',
                              userDetailDialog.data.company.maquilero_confeccion && 'Confección',
                              userDetailDialog.data.company.maquilero_acabados && 'Acabados',
                              userDetailDialog.data.company.maquilero_bordado && 'Bordado',
                              userDetailDialog.data.company.maquilero_estampado && 'Estampado',
                              userDetailDialog.data.company.maquilero_tenido && 'Teñido',
                              userDetailDialog.data.company.maquilero_otros && 'Otros',
                            ].filter(Boolean).join(', ') || 'No especificado'}
                          </div>
                        )}

                        {userDetailDialog.data.company.company_id && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full mt-2"
                            onClick={() => window.open(`/company/${userDetailDialog.data.company.company_id}`, '_blank')}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Ver Perfil Público
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 italic">Sin empresa registrada</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-emerald-700">{userDetailDialog.data.stats?.product_count || 0}</p>
                    <p className="text-xs text-emerald-600">Productos</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-amber-700">{userDetailDialog.data.stats?.surplus_count || 0}</p>
                    <p className="text-xs text-amber-600">Saldos</p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-blue-700">{userDetailDialog.data.stats?.quotes_received || 0}</p>
                    <p className="text-xs text-blue-600">Cotizaciones Recibidas</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold text-purple-700">{userDetailDialog.data.stats?.referrals_count || 0}</p>
                    <p className="text-xs text-purple-600">Referidos</p>
                  </CardContent>
                </Card>
              </div>

              {/* Products */}
              <Card>
                <CardContent className="pt-4">
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Productos en Catálogo ({userDetailDialog.data.products?.length || 0})
                  </h3>
                  {userDetailDialog.data.products?.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-60 overflow-y-auto">
                      {userDetailDialog.data.products.map((p) => (
                        <div key={p.product_id} className="border rounded-lg p-2 text-sm">
                          {p.images?.[0] ? (
                            <img src={p.images[0]} alt={p.name} className="w-full h-20 object-cover rounded mb-2" />
                          ) : (
                            <div className="w-full h-20 bg-slate-100 rounded mb-2 flex items-center justify-center">
                              <Package className="w-6 h-6 text-slate-300" />
                            </div>
                          )}
                          <p className="font-medium truncate">{p.name}</p>
                          <p className="text-xs text-slate-500">{p.category}</p>
                          {p.price && <p className="text-xs text-emerald-600">${p.price}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic text-center py-4">Sin productos publicados</p>
                  )}
                </CardContent>
              </Card>

              {/* Surplus */}
              {userDetailDialog.data.surplus_items?.length > 0 && (
                <Card>
                  <CardContent className="pt-4">
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4" />
                      Saldos Publicados ({userDetailDialog.data.surplus_items.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-40 overflow-y-auto">
                      {userDetailDialog.data.surplus_items.map((s) => (
                        <div key={s.surplus_id} className="border rounded-lg p-2 text-sm bg-amber-50">
                          <p className="font-medium truncate">{s.name}</p>
                          <p className="text-xs text-amber-600">{s.surplus_type}</p>
                          {s.price && <p className="text-xs text-slate-600">${s.price}</p>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <p className="text-center py-8 text-slate-500">No se encontraron datos</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
